// Showing a reviewer the page a staged number came from.
//
// The review already says where every rate came from — `/page/3/Table/3`, cell
// C7, "page 3" — which is precise and unreadable. What a person asks is "show
// me", and the only answer that settles it is the page with a box around the
// table.
//
// This is the SAME viewer the proposal side uses. Its job — draw a page, outline
// a block, let someone zoom and page around — has nothing to do with a document
// belonging to a project, so the only thing that changes here is where the page
// comes from: a tenant's artifact through baltigo, rather than a project's
// attachment through zou.
import { jsx as _jsx } from "react/jsx-runtime";
import { Testids } from '@mono/ui-selectors';
import { useQuery } from '@tanstack/react-query';
import { DocumentViewerDrawer } from '../../../components/ProposalDashboard/DocumentViewer';
import { govconClient } from '../../../lib/connect/clients';
/**
 * Loads one page of an uploaded pricing sheet.
 *
 * Signature matches the project-scoped loader so the viewer cannot tell the
 * difference: `projectId` is the TENANT here, and `docId` the source artifact.
 */ const usePricingSheetPage = (tenantId, artifactId, pageNum)=>{
    const { data, isLoading, error } = useQuery({
        enabled: !!tenantId && !!artifactId && pageNum > 0,
        queryKey: [
            'pricing sheet page',
            tenantId,
            artifactId,
            pageNum
        ],
        queryFn: async ()=>{
            const resp = await govconClient.getPricingSheetPage({
                pageNumber: pageNum,
                sourceDocumentId: artifactId,
                tenantId
            });
            const page = {
                imageUrl: resp.imageDataUrl,
                pageNumber: resp.pageNumber,
                totalPages: resp.totalPages,
                dimensions: {
                    width: resp.pageWidth,
                    height: resp.pageHeight
                },
                // Already scaled into the rendered image's pixel space by the handler,
                // which is the one place that knows both the converter's resolution and
                // the render's. A viewer that had to reconcile the two would drift.
                blocks: resp.blocks.map((b)=>({
                        id: b.blockId,
                        type: b.type,
                        content: b.content,
                        boundingBox: {
                            x: b.x,
                            y: b.y,
                            width: b.width,
                            height: b.height
                        },
                        // The parser does not grade a block's GEOMETRY — it grades the reading —
                        // so claiming a number here would be inventing one.
                        confidence: 1
                    }))
            };
            return page;
        },
        // A rendered page does not change: the artifact is immutable and a re-read
        // produces the same geometry. Re-fetching a megabyte of base64 PNG because a
        // reviewer paged back one is pure waste.
        staleTime: 600000
    });
    return {
        data,
        isLoading,
        error
    };
};
export function PricingSheetSourceDrawer({ besideDrawerWidth, blockId, companyId, filename, isOpen, onClose, page, sourceDocumentId }) {
    return /*#__PURE__*/ _jsx(DocumentViewerDrawer, {
        "data-testid": Testids.pricingSheetReview.sourceDrawer,
        document: {
            documentId: sourceDocumentId,
            filename
        },
        highlightBlockId: blockId,
        initialPage: page && page > 0 ? page : 1,
        // Fit the page, not 100%. A letter page at 100% is taller than a laptop
        // viewport, so opening there shows its top third — and the block a person
        // asked to see is usually not in it.
        initialZoom: 0.5,
        isOpen: isOpen,
        mode: besideDrawerWidth ? 'extension' : 'standalone',
        onClose: onClose,
        pageLoader: usePricingSheetPage,
        parentDrawerWidth: besideDrawerWidth,
        // The loader reads this as the tenant. Named projectId because the viewer's
        // prop is — renaming it there would touch every proposal-side caller for a
        // word.
        projectId: companyId
    });
}
